import { useState } from "react";
import { Flag, X, Check } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiMutate } from "@/lib/api";
import { NativeSelect } from "@/components/ui/NativeSelect";

interface FeedbackButtonProps {
  page: string;
  section?: string;
}

export function FeedbackButton({ page, section }: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("issue");
  const [description, setDescription] = useState("");
  const [sent, setSent] = useState(false);

  const submit = useMutation({
    mutationFn: (data: {
      page: string;
      section?: string;
      category: string;
      description: string;
      filters?: string;
    }) => apiMutate("/api/updates/feedback", "POST", data),
    onSuccess: () => {
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setDescription("");
      }, 1500);
    },
  });

  if (sent) {
    return (
      <span className="text-emerald-600 dark:text-emerald-400">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="rounded p-1 text-muted-foreground/30 hover:bg-muted hover:text-foreground transition-colors"
        title="Report an issue or suggest a change"
      >
        <Flag className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-50 w-72 rounded-lg border border-border bg-card p-4 shadow-xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Send Feedback</span>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <NativeSelect
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
            wrapperClassName="w-full mb-2"
          >
            <option value="issue">Data seems wrong</option>
            <option value="missing">Missing data</option>
            <option value="feature">Feature request</option>
            <option value="other">Other</option>
          </NativeSelect>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue or suggestion..."
            rows={3}
            autoFocus
            className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm mb-2 resize-none placeholder:text-muted-foreground/25"
          />
          <button
            onClick={() =>
              submit.mutate({
                page,
                section,
                category,
                description,
                filters: window.location.search,
              })
            }
            disabled={!description.trim() || submit.isPending}
            className="w-full rounded bg-primary py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {submit.isPending ? "Sending..." : "Submit Feedback"}
          </button>
        </div>
      )}
    </div>
  );
}
